import { Volume2, Square } from 'lucide-react';
import { useTTS } from '../hooks/useTTS';
import type { Database } from '../types/database';
import { cn } from '@/lib/utils';
import { supabase, supabaseEh } from '@/lib/supabase';

type Message = Database['eh']['Tables']['messages']['Row'];
type Character = Database['eh']['Tables']['characters']['Row'];

interface TTSButtonProps {
  character: Character | null;
  isSubscriber: boolean;
  message: Message;
  className?: string;
  onLoginRequired?: () => void;
  onTTSUsage?: (params?: { limit: 'low_gem_balance' | 'rate_limit' }) => void;
}

export const TTSButton = ({ character, isSubscriber, message, className = '', onLoginRequired, onTTSUsage }: TTSButtonProps) => {
  const { playTTS, getTTSState } = useTTS();
  const { isLoading, isPlaying, error } = getTTSState(message.id);

  // Only show TTS for AI messages with text
  if (!message.msg_text || message.sender_id || message.msg_type !== 'text') {
    return null;
  }

  const handleClick = async () => {
    // Check if user is logged in before proceeding
    const user = (await supabase.auth.getSession()).data.session?.user;
    if (!user) {
      onLoginRequired?.();
      return;
    }

    if (!isSubscriber) {
      // if limit reached (1 free tts message in last 24 hours), call onTTSUsage
      const { data, error } = await supabaseEh
        .from('media_requests')
        .select('created_at')
        .eq('user_id', user.id)
        .eq('media_type', 'tts')
        .eq('status', 'success')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .limit(1);
  
      if (error) {
        if (import.meta.env.DEV) console.error('[DEV] Failed to check media_requests:', error);
        return;
      } else if (data && data.length >= 1) {
        onTTSUsage?.();
        return;
      }
    }

    const result = await playTTS(message, character);
    
    // TODO: handle rate limit for subscribers
    if (result.limitReached && result.limitType === 'low_gem_balance') {
      onTTSUsage?.({ limit: 'low_gem_balance' });
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(`flex items-center justify-center size-8 rounded-full
        bg-zinc-700 hover:bg-zinc-600 disabled:opacity-80 disabled:cursor-not-allowed
        text-zinc-300 hover:text-white transition-colors`,
        className
      )}
      title={isPlaying ? 'Stop speech' : 'Play speech'}
      aria-label={isPlaying ? 'Stop speech' : 'Play speech'}
    >
      {isLoading ? (
        <span className="size-4 border-4 border-zinc-500 border-t-rose-500 rounded-full animate-spin"></span>
      ) : isPlaying ? (
        <Square className="size-3" />
      ) : error ? (
        <Volume2 className="size-4 text-red-400" />
      ) : (
        <Volume2 className="size-4" />
      )}
    </button>
  );
}; 